import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useServicesStore } from '@/stores/servicesStore';
import { useTeamsStore } from '@/stores/teamsStore';
import { ErrorAlert } from '@nimbitlabs/ui';
import type { ServiceFormData } from '@/types/service';
import { ServiceStatus, ServiceType } from '@/types/api';
import { ArrowLeft, Package, Users } from 'lucide-react';
import { Button, Card, CardContent, CardDescription, CardHeader, CardTitle, Input, Label, Select, SelectContent, SelectItem, SelectTrigger, SelectValue, TagInput, Textarea } from '@nimbitlabs/ui';

export const CreateService: React.FC = () => {
  const navigate = useNavigate();
  const { teams: allTeams } = useTeamsStore();
  const { addService } = useServicesStore();
  
  const [formData, setFormData] = useState<ServiceFormData>({
    name: '',
    description: '',
    version: '1.0.0',
    status: ServiceStatus.Healthy,
    type: ServiceType.Api,
    teamId: '',
    tags: []
  });
  
  const [error, setError] = useState<unknown>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    
    try {
      await addService(formData);
      navigate('/services');
    } catch (err) {
      console.error('Failed to create service:', err);
      setError(err);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="outline" size="sm" asChild>
          <Link to="/services">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Services
          </Link>
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Create New Service</CardTitle>
          <CardDescription>
            Create a new service in your service catalog.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {/* Error Display */}
          <ErrorAlert error={error} className="mb-4" />
          
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="name">Service Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="My Service"
                  required
                  maxLength={200}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="type">Type *</Label>
                <Select value={formData.type} onValueChange={(value) => setFormData({ ...formData, type: value as ServiceFormData['type'] })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={ServiceType.Api}>API</SelectItem>
                    <SelectItem value={ServiceType.Microservice}>Microservice</SelectItem>
                    <SelectItem value={ServiceType.Database}>Database</SelectItem>
                    <SelectItem value={ServiceType.Frontend}>Frontend</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description *</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Describe what this service does..."
                required
                maxLength={1000}
                rows={4}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="team">Team *</Label>
              {allTeams.length === 0 ? (
                <Button
                  type="button"
                  variant="outline"
                  className="w-full justify-start"
                  asChild
                >
                  <Link to="/teams">
                    <Users className="h-4 w-4 mr-2" />
                    Create your first team
                  </Link>
                </Button>
              ) : (
                <Select value={formData.teamId} onValueChange={(value) => setFormData({ ...formData, teamId: value })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select a team" />
                  </SelectTrigger>
                  <SelectContent>
                    {allTeams.map((team) => (
                      <SelectItem key={team.id} value={team.id}>
                        {team.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>

            <div className="space-y-2">
              <Label>Tags</Label>
              <TagInput
                tags={formData.tags}
                onTagsChange={(tags) => setFormData({ ...formData, tags })}
                placeholder="Add tags (e.g. frontend, api, production)"
              />
            </div>

            <div className="flex justify-end gap-3">
              <Button type="button" variant="outline" onClick={() => navigate('/services')}>
                Cancel
              </Button>
              <Button type="submit">
                Create Service
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};
